import {
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  CONDITION_AND_MILEAGE,
  REPORT,
  LICENSE_PLATE_AND_VIN,
  PARTNER_OFFER_CONDITIONS,
  EMAIL,
  INSTANT_OFFER,
  LICENSE_PLATE_AND_VIN_SECOND_CHANCE_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';

import { getQuery } from 'client/utils/location';
import { fireMinMaxRangeTracking } from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-tracking-utils';
import {
  getStepTitle,
  getPredefinedVin,
  getTransmissionData,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import { noop } from 'lodash-es';

const EMPTY_STEP_CONFIG = {};

/**
 * Gets data for location and style step.
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @returns {Object}
 */
export function getLocationAndStyleData(step, state, props) {
  if (step.id !== LOCATION_AND_STYLE) return EMPTY_STEP_CONFIG;

  const {
    zipCode,
    isZipCodeValid,
    errorMap,
    styleIds,
    vehicle,
    vin,
    isAllowedToPredefineVin,
    isLoadingColorsAndOptions,
    steps,
  } = state;
  const { estimatedVehicle, isExtendedReport, isMobile, params, isHighlightsPage, location } = props;

  const { vin: queryVin, isQueryPredefinedVin } = getPredefinedVin(location);
  const nextStepTitle = getStepTitle(steps, step.nextId);

  return {
    zipCode,
    estimatedVehicle,
    vehicle,
    isZipCodeValid,
    errorMap,
    isExtendedReport,
    isMobile,
    styleIds,
    vin,
    isQueryPredefinedVin,
    isStepActive: step.isActive,
    queryVin,
    isAllowedToPredefineVin,
    isLoadingColorsAndOptions,
    nextStepTitle,
    params,
    isHighlightsPage,
  };
}

/**
 * Gets data for options and colors step.
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @param {Object} helpers
 * @returns {Object}
 */
export function getOptionsAndColorsData(step, state, props, helpers) {
  if (![LICENSE_PLATE_AND_VIN, COLORS_AND_OPTIONS].includes(step.id)) return EMPTY_STEP_CONFIG;

  const {
    vehicle,
    errorMap,
    isZipCodeValid,
    transmissionTypeFromLastQuote,
    prefilledExteriorColorId,
    prefilledInteriorColorId,
    prefilledOptions,
    vin,
    onColorsAndOptionsLoaded,
    isLoadingColorsAndOptions,
    exteriorColor,
    interiorColor,
  } = state;
  const { isExtendedReport, drawerCreativeId } = props;

  const activeStepId = helpers.getActiveStep().id;
  return {
    isStepActive: step.isActive && step.id === COLORS_AND_OPTIONS,
    isLocationStepActive: activeStepId === LOCATION_AND_STYLE,
    vehicle,
    errorMap,
    isExtendedReport,
    isZipCodeValid,
    isLoadingColorsAndOptions,
    drawerCreativeId,
    transmissionType: transmissionTypeFromLastQuote,
    prefilledExteriorColorId,
    prefilledInteriorColorId,
    prefilledOptions,
    vin,
    isStepDone: step.isDone,
    onColorsAndOptionsLoaded,
    exteriorColor,
    interiorColor,
  };
}

/**
 * Gets data for condition and mileage step.
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @returns {Object}
 */
export function getConditionAndMileageData(step, state, props) {
  if (step.id !== CONDITION_AND_MILEAGE) return EMPTY_STEP_CONFIG;

  const {
    mileage,
    condition,
    isMileageValid,
    errorMap,
    vehicle: { style: styleObj },
    transmissionType,
    engine,
    isTransmissionTypeTranslationOverride,
    isEngineSizeTranslationOverride,
  } = state;
  const { isExtendedReport } = props;
  const { year } = styleObj || {};

  return {
    mileage,
    condition,
    isMileageValid,
    errorMap,
    isStepActive: step.isActive,
    transmissionType,
    engine,
    isTransmissionTypeTranslationOverride,
    isEngineSizeTranslationOverride,
    year,
    isExtendedReport,
  };
}

/**
 * Gets data for appraisal report step.
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @param {Object} helpers
 * @returns {Object}
 */
export function getAppraisalReportData(step, state, props, helpers) {
  if (![INSTANT_OFFER, REPORT].includes(step.id)) return EMPTY_STEP_CONFIG;

  const {
    mileage,
    exteriorColor,
    condition,
    options,
    partnerEquipment,
    zipCode,
    isZipCodeValid,
    isMileageValid,
    emailReportStatus,
    selectedReportType,
    vin,
    showAddEquipment,
  } = state;
  const {
    isMobile,
    isExtendedReport,
    sellYourCarConfig,
    sellingPrice,
    msrp,
    residualValue,
    isPurchase,
    isUsed,
    onDone,
    location,
    displayTradeInValues,
  } = props;
  const { vin: queryVin } = getPredefinedVin(location);
  const hasPartnerEquipment = partnerEquipment.length > 0;

  return {
    getVehicleData: helpers.getVehicleData,
    options,
    showAddEquipment,
    hasPartnerEquipment,
    color: exteriorColor,
    mileage,
    condition,
    zipCode,
    isExtendedReport,
    isMobile,
    isZipCodeValid,
    isMileageValid,
    isAllStepsCompleted: helpers.checkStepsCompleteness(),
    emailReportStatus,
    sellYourCarConfig,
    isReportActive: step.isActive && step.id === REPORT,
    stepNextId: step.nextId,
    sellingPrice,
    msrp,
    residualValue,
    isPurchase,
    queryVin,
    isUsed,
    selectedReportType,
    vin,
    toggleAppraiserDrawer: onDone,
    displayTradeInValues,
  };
}

/**
 * License Plate Step data/props
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @param {Object} helpers
 * @returns {Object}
 */
export function getLicensePlateAndVinData(step, state, props, helpers) {
  if (![INSTANT_OFFER, LICENSE_PLATE_AND_VIN].includes(step.id)) return EMPTY_STEP_CONFIG;

  const { nextId, isDone } = step;
  const {
    vin: vinFromState,
    vehicle,
    isVinFromDrawer,
    hasVinMismatchError,
    isAllowedToPredefineVin,
    decodedVinEligibility,
    options,
    zipCode,
    mileage,
    condition,
    exteriorColor,
    interiorColor,
    isLoadingColorsAndOptions,
  } = state;
  const { isExtendedReport, isMobile, params, location } = props;
  const { vin: queryVin, isQueryPredefinedVin } = getPredefinedVin(location);
  const activeStepId = helpers.getActiveStep().id;

  return {
    vehicle,
    isStepActive: step.isActive && step.id === LICENSE_PLATE_AND_VIN,
    stepNextId: nextId,
    vin: vinFromState,
    isExtendedReport,
    options,
    zipCode,
    mileage,
    condition,
    activeStepId,
    exteriorColor,
    interiorColor,
    colorsAndOptionsProps: getOptionsAndColorsData(step, state, props, helpers),
    isLoadingColorsAndOptions,
    queryVin,
    isQueryPredefinedVin,
    isVinFromDrawer,
    hasVinMismatchError,
    isStepDone: isDone,
    isMobile,
    isAllowedToPredefineVin,
    decodedVinEligibility,
    params,
  };
}

/**
 * Offer Conditions Step data/props
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @param {Object} helpers
 * @returns {Object}
 */
export function getOfferConditionsData(step, state, props, helpers) {
  if (step.id !== PARTNER_OFFER_CONDITIONS) return EMPTY_STEP_CONFIG;

  const { errorMap, vin, vehicle, isVinFromDrawer, partnerQuestionnaireAnswers } = state;
  const { location } = props;
  const { vin: predefinedVin } = getPredefinedVin(location);

  return {
    isStepActive: step.isActive,
    vehicle,
    vin,
    errorMap,
    isVinFromDrawer,
    predefinedVin,
    activeStepId: helpers.getActiveStep().id,
    savedQuestionsAnswered: partnerQuestionnaireAnswers,
    modsId: getQuery(location).modsId,
  };
}

/**
 * Email Step data/props
 * @param {Object} step
 * @param {Object} state
 * @returns {Object}
 */
export function getEmailStepData(step, state) {
  if (step.id !== EMAIL) return EMPTY_STEP_CONFIG;

  const { isEmailDataReady, steps } = state;
  const isStepActive = step?.isActive;

  return {
    isStepActive,
    isEmailDataReady,
    stepCounter: steps.indexOf(step) + 1,
    nextStepTitle: getStepTitle(steps, step?.nextId),
  };
}

/**
 * Instant Offer Step data/props
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @param {Object} helpers
 * @returns {Object}
 */
export function getInstantOfferData(step, state, props, helpers) {
  const { isMobile, setAppraiseFinished, onDone, modsData, vinStyles } = props;
  const { transmissionType, isTransmissionTypeSelectedByUser } = state;

  if (step.id !== INSTANT_OFFER) return EMPTY_STEP_CONFIG;

  const {
    exteriorColor,
    condition,
    vin,
    partnerOfferMileage,
    mileage,
    options,
    zipCode,
    emailReportStatus,
    vehicle,
    decodedVinEligibility,
    emailFromEmailStep,
    appraisalEmailData,
  } = state;

  const isStepActive = step.isActive && !!vin;
  const { transmissionSource } = getTransmissionData({
    vinStyles,
    transmissionType,
    isTransmissionTypeSelectedByUser,
  });

  return {
    transmissionSource,
    condition,
    color: exteriorColor,
    partnerOfferMileage,
    mileage,
    vin,
    options,
    zipCode,
    isStepActive,
    fireMinMaxRangeTracking,
    emailReportStatus,
    vehicle,
    isMobile,
    setAppraiseFinished,
    isStepFinal: step.isFinal,
    decodedVinEligibility,
    toggleAppraiserDrawer: onDone,
    modsData,
    isEmailStepActive: helpers.getActiveStep().id === EMAIL,
    emailFromEmailStep,
    noAppraisalReport: !appraisalEmailData?.isReportAvailable,
  };
}

/**
 * Escape Hatch data/props
 * @param {Object} step
 * @param {Object} state
 * @param {Object} props
 * @param {Object} helpers
 * @returns {Object}
 */
export function getEscapeHatchData(step, state, props, helpers) {
  const { setAppraiseFinished } = props;

  if (step.id !== INSTANT_OFFER) return EMPTY_STEP_CONFIG;

  const isStepActive = step.isActive;
  const { vin } = state;
  const adjustLicensePlateAndVinProps = getLicensePlateAndVinData(step, state, props, helpers);

  const adjustedAppraisalReportProps = {
    ...getAppraisalReportData(step, state, props, helpers),
    isEscapeHatch: true,
    isReportActive: isStepActive,
    licensePlateAndVinProps: {
      ...adjustLicensePlateAndVinProps,
      trackingCreativeId: LICENSE_PLATE_AND_VIN_SECOND_CHANCE_CREATIVE_ID,
      updateVinAndContinue: helpers.updateVinAndContinueEscapeHatch,
      clearVin: helpers.clearVinEscapeHatch,
      onValueChange: noop,
      isStepActive,
      stepId: step.id,
    },
  };

  return {
    isStepActive: isStepActive && !vin,
    vin,
    appraisalReportProps: adjustedAppraisalReportProps,
    setAppraiseFinished,
    isStepFinal: step.isFinal,
  };
}
